// Stable per-browser id, sent as the `x-device-id` header on every API call and used by the
// backend as the visitorId on Google Vertex user events.
//
// WHY NOT session_token: api.service.js blanks session_token the moment a shopper logs in
// (`headers.common["session_token"] = ""`), and it lives in sessionStorage so it dies with the
// tab. Either alone breaks the chain Vertex learns from — a shopper who searches as a guest and
// then logs in to pay would have the purchase filed under a different visitor than the searches
// that led to it. This id is in localStorage and is never cleared on login or logout.
//
// WHY IT MAY RETURN NULL, AND WHY THAT IS FINE: no id means the backend records no event. That
// is deliberate on both sides — a shared placeholder id would be worse than silence, because
// Google needs 100+ *unique* visitor ids before the data validates at all, so one fake id
// shared across everyone can invalidate every real event beside it.
//
// NOTHING HERE MAY THROW. It runs inside updateHeader() on the path of every single API call,
// so an exception escaping this file would take down the whole storefront — not just events.
// localStorage genuinely throws (not just returns null) in Safari private mode and wherever
// site data is blocked, and crypto.randomUUID is undefined on plain http:// origins, which the
// backend's CORS list still allows. Both are handled, neither is theoretical.

// Deliberately NO import of ./consent, even though isAllowed() is exactly the function wanted
// here. consent.js imports api.service.js for its server sync, and api.service.js imports this
// file — so importing consent would close the loop api.service → deviceId → consent →
// api.service. A circular ES import leaves one side holding partially-initialised exports, and
// the failure is timing-dependent, which is the worst kind to debug on a storefront. Reading
// the one key directly keeps this module dependency-free. The key and the shape must stay in
// step with consent.js: { analytics, advertising, functional, version, ts }.
const CONSENT_KEY = "cookieConsent";

export const DEVICE_ID_KEY = "wc_device_id";
export const DEVICE_ID_HEADER = "x-device-id";

// Holds the id for this page load when localStorage is readable but not WRITABLE — Safari
// private mode, or a filled quota. Without it, every failed write would mint a fresh id and each
// API call in the same page load would look like a different visitor: worse than sending nothing,
// because it inflates Google's unique-visitor count with shopper journeys one action long.
// Not a cache for the normal path — localStorage is already the store there.
let fallbackId = null;

// The branded domain's id, carried into the Razorpay hand-off page (a different origin, so a
// different localStorage with no consent record). The shopper consented on the branded domain —
// that is the only way an id travels — so this page reports the purchase under that same id.
// Memory only: never written to this origin's storage, which the shopper never consented on.
let handoffId = null;

// The only shapes newDeviceId() produces: a UUID or `wc-<base36>-<base36>`. Anything else in
// the URL is ignored, so a crafted link cannot put arbitrary text into a request header.
const DEVICE_ID_SHAPE = /^[A-Za-z0-9-]{8,64}$/;

export function setHandoffDeviceId(id) {
  handoffId = typeof id === "string" && DEVICE_ID_SHAPE.test(id) ? id : null;
}

// RFC 4122 v4. crypto.randomUUID() is the right call but needs a SECURE CONTEXT — on
// http:// it is simply not there — so getRandomValues is tried next, and Math.random last.
// The last rung is not cryptographically sound and does not need to be: this is a bucket
// label for ranking, not a secret or a session token. Uniqueness is all that is asked of it.
export function newDeviceId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
      bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch (e) {
    /* fall through to the last resort below */
  }
  return `wc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// The id for this browser, or null when we must not have one.
//
// Read-then-create, never regenerate: an id that changes is worse than no id, because it
// splits one shopper's history into fragments that each look like a visitor who did one thing
// and left.
//
// Gated on ANALYTICS consent, matching how the Meta pixel is gated on `advertising` in
// HelperFunctions.js. A permanent behavioural identifier is exactly what that category is for,
// and the gate needs no backend counterpart: no consent → no header → the backend already
// skips the event by itself.
export function getDeviceId() {
  try {
    if (handoffId) return handoffId;
    if (!analyticsAllowed()) return null;

    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;

    if (fallbackId) return fallbackId;

    const created = newDeviceId();
    try {
      localStorage.setItem(DEVICE_ID_KEY, created);
      return created;
    } catch (e) {
      // Readable but not writable. Keep the id in memory so this page load stays ONE visitor;
      // the next load starts fresh, which is a shorter chain rather than a corrupt one.
      fallbackId = created;
      return fallbackId;
    }
  } catch (e) {
    // localStorage threw outright — site data blocked entirely. No id, no events, no error
    // reaching the caller.
    return null;
  }
}

// Mirrors consent.js isAllowed("analytics"): there is no consent banner, so no record means
// "never opted out" and is allowed. Only an explicit `analytics: false`, saved from
// /cookie-preferences, turns it off. This said the opposite until the banner was removed on
// 11 Sep 2026 and was never updated, so every browser without a saved record got no id and sent
// no Vertex events -- about 40% of searches (385 searched vs 232 reached Vertex, 16 Sep-16 Oct).
// A record we cannot read stays "no": we cannot tell whether it says the shopper opted out.
// Kept private to this module — the export surface stays the id, not the consent question.
function analyticsAllowed() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return true;
    return JSON.parse(raw).analytics !== false;
  } catch (e) {
    return false;
  }
}
